/**
 * ヤギプライム決済の GA4 送信判定の正本。
 *
 * 決済の入口は main（PaymentMethodSelector）/ font（YagiprimeFontPaymentDialog）/
 * quickemail（YagiPrimeQuickEmailBuy）の 3 つあるが、どれも subscription_type=1 /
 * 550円(税込) / 30日の同一サブスク。GA4 へは purchase 1 種類だけを送り、入口は
 * entry_point で区別する。
 *
 * 各コンポーネントは「決済実行前に markYagiprimePurchasePending で文脈を置く」
 * 「成立時に trackYagiprimePurchase を呼ぶ」だけにし、送るか否かの判定
 * （プラン・トライアル・重複）はすべてこのモジュールに集約する。
 *
 * pending を sessionStorage に置くのは、PayPay / Stripe 3DS・ApplePay が
 * 外部リダイレクトを挟むため。復帰後のページでも金額と入口を復元できる。
 */

export const YAGIPRIME_PLAN_30D = Object.freeze({
  subscriptionType: 1,
  price: 550,
  tax: 50,
  days: 30,
  itemId: 'yagiprime_30d',
  itemName: 'ヤギプライム 30日'
})

export const YAGIPRIME_ENTRY_POINTS = Object.freeze({
  main: 'main',
  font: 'font',
  quickemail: 'quickemail'
})

export type YagiprimeEntryPoint = 'main' | 'font' | 'quickemail'

export type YagiprimePurchasePending = {
  entryPoint: YagiprimeEntryPoint
  subscriptionType: number
  paymentMethod: string
  price: number
  screenPath: string
  timestamp: number
}

export type YagiprimePurchaseGtm = {
  sendYagiprimePurchase: (data: Record<string, unknown>) => void
  sendYagiprimePaypayAuthorized: (data: Record<string, unknown>) => void
}

const PENDING_KEY = '_YAGISH_YAGIPRIME_PURCHASE_PENDING'
const SENT_KEY = '_YAGISH_YAGIPRIME_PURCHASE_SENT'
/** リダイレクト決済の往復を見込んだ pending の有効期限 */
const PENDING_TTL_MS = 30 * 60 * 1000
/** 送信済み transaction_id の保持上限（sessionStorage 肥大化の防止） */
const SENT_ID_LIMIT = 20

const isDev = process.env.NODE_ENV === 'development'

function debugLog(label: string, payload?: unknown): void {
  if (isDev) {
    console.log(`[GTM Yagiprime] ${label}`, payload)
  }
}

function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch (error) {
    debugLog('sessionStorage 読み込み失敗', error)
    return null
  }
}

function writeSession(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value)
  } catch (error) {
    debugLog('sessionStorage 書き込み失敗', error)
  }
}

function removeSession(key: string): void {
  try {
    window.sessionStorage.removeItem(key)
  } catch (error) {
    debugLog('sessionStorage 削除失敗', error)
  }
}

/**
 * 決済実行・外部リダイレクトの直前に呼ぶ。ここで置かれた文脈が無い成功通知
 * （加入済みチェック由来の success など）は購入として送らない。
 */
export function markYagiprimePurchasePending(input: {
  entryPoint: YagiprimeEntryPoint
  subscriptionType?: number | string | null
  paymentMethod?: string | null
  price?: number | string | null
  screenPath?: string | null
}): void {
  const pending: YagiprimePurchasePending = {
    entryPoint: input.entryPoint,
    subscriptionType: toNumber(
      input.subscriptionType,
      YAGIPRIME_PLAN_30D.subscriptionType
    ),
    paymentMethod: input.paymentMethod || 'card',
    price: toNumber(input.price, YAGIPRIME_PLAN_30D.price),
    screenPath: input.screenPath || '',
    timestamp: Date.now()
  }
  writeSession(PENDING_KEY, JSON.stringify(pending))
  debugLog('pending 記録', pending)
}

/** pending を取り出して削除する。無い / 期限切れなら null */
function takeYagiprimePurchasePending(): YagiprimePurchasePending | null {
  const raw = readSession(PENDING_KEY)
  if (!raw) {
    return null
  }
  removeSession(PENDING_KEY)

  let pending: YagiprimePurchasePending
  try {
    pending = JSON.parse(raw)
  } catch (error) {
    debugLog('pending の解析に失敗', error)
    return null
  }

  if (!pending || typeof pending !== 'object') {
    return null
  }
  if (Date.now() - toNumber(pending.timestamp, 0) > PENDING_TTL_MS) {
    debugLog('pending の有効期限切れ', pending)
    return null
  }
  return pending
}

/** 決済失敗・重複加入（409）などで、購入として扱わない場合に文脈を捨てる */
export function clearYagiprimePurchasePending(): void {
  removeSession(PENDING_KEY)
}

/**
 * 課金確定時に呼ぶ。カード決済は CardPayment と PaymentMethodSelector の
 * 二重通知があるため、transaction_id 単位で送信済みを記録して重複を防ぐ。
 */
export function trackYagiprimePurchase(
  gtm: YagiprimePurchaseGtm | null | undefined,
  input: { transactionId?: string | null; uuid?: string | null }
): boolean {
  const pending = takeYagiprimePurchasePending()
  if (!pending) {
    debugLog('pending が無いため purchase を送らない')
    return false
  }
  if (pending.subscriptionType !== YAGIPRIME_PLAN_30D.subscriptionType) {
    debugLog('対象外プランのため purchase を送らない', pending)
    return false
  }
  const transactionId = buildTransactionId(input.transactionId, input.uuid)
  if (!markSent(transactionId)) {
    debugLog('送信済みのため purchase をスキップ', transactionId)
    return false
  }
  if (!gtm) {
    return false
  }

  gtm.sendYagiprimePurchase({
    transaction_id: transactionId,
    value: pending.price,
    tax: YAGIPRIME_PLAN_30D.tax,
    entry_point: pending.entryPoint,
    payment_method: pending.paymentMethod,
    screen_path: pending.screenPath
  })
  return true
}

/**
 * PayPay は認可完了までしかフロントで分からず、実課金は webhook 非同期のため
 * purchase ではなく専用イベントで送る。
 */
export function trackYagiprimePaypayAuthorized(
  gtm: YagiprimePurchaseGtm | null | undefined,
  input: { authorizationId?: string | null }
): boolean {
  const pending = takeYagiprimePurchasePending()
  if (!pending) {
    debugLog('pending が無いため paypay_authorized を送らない')
    return false
  }

  const authorizationId = String(
    input.authorizationId || `paypay_${Date.now()}`
  )
  if (!markSent(authorizationId)) {
    debugLog('送信済みのため paypay_authorized をスキップ', authorizationId)
    return false
  }
  if (!gtm) {
    return false
  }

  gtm.sendYagiprimePaypayAuthorized({
    authorization_id: authorizationId,
    value: pending.price,
    entry_point: pending.entryPoint,
    screen_path: pending.screenPath
  })
  return true
}

function buildTransactionId(
  transactionId?: string | null,
  uuid?: string | null
): string {
  if (transactionId) {
    return String(transactionId)
  }
  return `yagiprime_${uuid || 'unknown'}_${Date.now()}`
}

/** 未送信なら記録して true。既に送信済みなら false */
function markSent(id: string): boolean {
  const raw = readSession(SENT_KEY)
  let sent: string[] = []
  if (raw) {
    try {
      const parsed = JSON.parse(raw)
      sent = Array.isArray(parsed) ? parsed.map(String) : []
    } catch (error) {
      debugLog('送信済みIDの解析に失敗', error)
    }
  }
  if (sent.indexOf(id) !== -1) {
    return false
  }
  sent.push(id)
  writeSession(SENT_KEY, JSON.stringify(sent.slice(-SENT_ID_LIMIT)))
  return true
}

function toNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number' && !isNaN(value)) {
    return value
  }
  // "1,350" のようなカンマ区切り文字列も数値化する
  const parsed = Number(String(value == null ? '' : value).replace(/,/g, ''))
  return isNaN(parsed) ? fallback : parsed
}
